import { useCallback, useEffect, useState } from "react";
import {
  fetchDoctorConsoleConfig,
  normalizeDoctorId,
} from "../api/doctorConsoleConfigApi";

/**
 * Fetches the published config override for a doctor so DoctorConsole can
 * pass it into DoctorConsoleConfigProvider's existing `config` prop.
 */
export const useDoctorConsoleRemoteConfig = (doctorId) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refetchToken, setRefetchToken] = useState(0);

  const normalizedDoctorId = normalizeDoctorId(doctorId);

  const loadConfig = useCallback(() => {
    if (!normalizedDoctorId) {
      setConfig(null);
      return Promise.resolve(null);
    }

    setLoading(true);
    setError(null);

    return fetchDoctorConsoleConfig(normalizedDoctorId)
      .then((data) => {
        setConfig(data?.config ?? null);
        return data?.config ?? null;
      })
      .catch((err) => {
        console.error("Failed to fetch doctor console config", err);
        setError(err);
        setConfig(null);
        return null;
      })
      .finally(() => setLoading(false));
  }, [normalizedDoctorId]);

  useEffect(() => {
    let cancelled = false;

    loadConfig().then((loaded) => {
      if (cancelled) return loaded;
      return loaded;
    });

    return () => {
      cancelled = true;
    };
  }, [loadConfig, refetchToken]);

  const refetch = useCallback(() => setRefetchToken((t) => t + 1), []);

  return { config, loading, error, refetch };
};
